import { useState, useEffect } from 'react';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const data = new URLSearchParams();
    
    formData.forEach((value, key) => {
      data.append(key, value.toString());
    });

    try {
      const response = await fetch('https://readdy.ai/api/form/d6e38q2ohb161tfd8r2g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: data.toString(),
      });

      if (response.ok) {
        alert('Message sent successfully!');
        form.reset();
      } else {
        alert('Failed to send message. Please try again.');
      }
    } catch (error) {
      alert('An error occurred. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-sm' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-20">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center gap-3">
              <img src="https://public.readdy.ai/ai/img_res/56d1bb7a-176a-4552-b93a-e2669969f21d.png" alt="PROTIM D.O.O." className="h-10 w-10 object-contain" />
              <span className="text-xl font-bold text-gray-900">PROTIM D.O.O.</span>
            </div>
            <div className="flex items-center gap-8">
              <button onClick={() => scrollToSection('home')} className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer">Home</button>
              <span className="text-gray-300">|</span>
              <button onClick={() => scrollToSection('about')} className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer">About</button>
              <span className="text-gray-300">|</span>
              <button onClick={() => scrollToSection('products')} className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer">Products</button>
              <span className="text-gray-300">|</span>
              <button onClick={() => scrollToSection('contact')} className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer">Contact</button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="pt-32 pb-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-6xl mx-auto px-8 text-center">
          <h1 className="text-6xl font-extrabold text-gray-900 leading-tight mb-6" style={{ fontFamily: 'Inter, sans-serif' }}>
            Modern iOS Applications<br />
            Built for Security<br />
            & Performance
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
            Expert team developing cutting-edge applications for the iOS platform,<br />
            enhancing user experience and mobile device security
          </p>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-24 bg-[#F8F6F3]">
        <div className="max-w-6xl mx-auto px-8">
          <div className="mb-12">
            <span className="text-xs font-semibold text-gray-500 tracking-widest uppercase">Our Products</span>
          </div>
          <div className="grid grid-cols-2 gap-10">
            {/* VPN Product */}
            <div className="bg-white rounded-3xl p-16 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-12 h-12 flex items-center justify-center mb-8">
                <i className="ri-shield-check-line text-5xl text-gray-900"></i>
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-5">VPN</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                Secure and fast VPN service for iOS devices ensuring complete privacy and data protection. Browse safely with military-grade encryption and enjoy unrestricted access to content worldwide.
              </p>
            </div>

            {/* Cleaner Product */}
            <div className="bg-white rounded-3xl p-16 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-12 h-12 flex items-center justify-center mb-8">
                <i className="ri-delete-bin-line text-5xl text-gray-900"></i>
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-5">Cleaner</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                Advanced cleaning tool that optimizes device performance by removing junk files and managing storage efficiently. Keep your iOS device running smoothly with intelligent cleanup algorithms.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-32 bg-[#2B2B2B]">
        <div className="max-w-5xl mx-auto px-8">
          <div className="flex items-center gap-16">
            <div className="flex-1">
              <div className="inline-block bg-purple-100 text-purple-700 px-4 py-2 rounded-full text-xs font-semibold mb-10">
                ABOUT US
              </div>
              <h2 className="text-5xl font-bold text-white mb-8 leading-tight">
                Expert Team Developing Modern iOS Solutions
              </h2>
              <div className="space-y-6 text-base text-white/85 leading-relaxed">
                <p>
                  PROTIM D.O.O. is a dedicated team of iOS development experts committed to creating innovative applications that prioritize user experience and security. Our approach combines technical excellence with deep understanding of user needs.
                </p>
                <p>
                  We believe in building products that not only solve problems but enhance the daily lives of our users. Every application we develop undergoes rigorous testing to ensure reliability, performance, and seamless functionality across all iOS devices.
                </p>
                <p>
                  Security and performance are at the core of everything we do. We implement industry-leading practices to protect user data while delivering lightning-fast experiences that users can depend on every day.
                </p>
              </div>
            </div>
            <div className="w-80 h-80 flex-shrink-0">
              <img 
                src="https://public.readdy.ai/ai/img_res/05805940d4c998c56d00e354fd042fd8.jpg" 
                alt="Team workspace" 
                className="w-full h-full object-cover rounded-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-white">
        <div className="max-w-6xl mx-auto px-8">
          <div className="flex items-start justify-between mb-16">
            <h2 className="text-4xl font-bold text-gray-900 max-w-md">Why Choose Us</h2>
            <p className="text-base text-gray-600 max-w-md leading-relaxed">
              We combine technical expertise with user-centric design to deliver iOS applications that stand out in the App Store
            </p>
          </div>
          
          <div className="grid grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div className="bg-gray-50 rounded-2xl p-10">
              <div className="w-8 h-8 flex items-center justify-center mb-6">
                <i className="ri-apple-line text-3xl text-gray-900"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">iOS Expertise</h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                Deep knowledge of iOS ecosystem, Swift, and Apple's design guidelines ensuring native performance and user experience
              </p>
            </div>

            {/* Feature 2 */}
            <div className="bg-gray-50 rounded-2xl p-10">
              <div className="w-8 h-8 flex items-center justify-center mb-6">
                <i className="ri-lock-line text-3xl text-gray-900"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Security Focus</h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                Implementation of advanced security protocols and encryption standards to protect user data and privacy at all times
              </p>
            </div>

            {/* Feature 3 */}
            <div className="bg-gray-50 rounded-2xl p-10">
              <div className="w-8 h-8 flex items-center justify-center mb-6">
                <i className="ri-user-heart-line text-3xl text-gray-900"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">User Experience</h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                Intuitive interfaces and smooth interactions designed to delight users while maintaining simplicity and functionality
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-[#F9F9F9]">
        <div className="max-w-5xl mx-auto px-8">
          <div className="text-center mb-16">
            <span className="text-sm font-semibold text-gray-500 tracking-widest uppercase">Contact Us</span>
            <h2 className="text-4xl font-bold text-gray-900 mt-5">Get In Touch</h2>
          </div>

          <div className="grid grid-cols-2 gap-16 mb-16">
            {/* Address */}
            <div>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-map-pin-line text-xl text-gray-600"></i>
                </div>
                <span className="text-sm font-semibold text-gray-600 uppercase tracking-wide">Address</span>
              </div>
              <div className="space-y-2 ml-8">
                <p className="text-lg text-gray-900">BORIS TRAJKOVSKI 21</p>
                <p className="text-lg text-gray-900">SKOPJE, 1000</p>
                <p className="text-lg text-gray-900">North Macedonia</p>
              </div>
            </div>

            {/* Email & Phone */}
            <div className="space-y-10">
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-mail-line text-xl text-gray-600"></i>
                  </div>
                  <span className="text-sm font-semibold text-gray-600 uppercase tracking-wide">Email</span>
                </div>
                <p className="text-lg text-gray-900 ml-8">info@protimdoo.com</p>
              </div>

              <div>
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-phone-line text-xl text-gray-600"></i>
                  </div>
                  <span className="text-sm font-semibold text-gray-600 uppercase tracking-wide">Phone</span>
                </div>
                <p className="text-lg text-gray-900 ml-8">+1 5075095863</p>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white rounded-2xl p-12 shadow-sm">
            <h3 className="text-2xl font-bold text-gray-900 mb-8">Send us a message</h3>
            <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                  <input 
                    type="text" 
                    id="name" 
                    name="name" 
                    required 
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent outline-none transition-all text-sm"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                  <input 
                    type="email" 
                    id="email" 
                    name="email" 
                    required 
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent outline-none transition-all text-sm"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">Subject</label>
                <input 
                  type="text" 
                  id="subject" 
                  name="subject" 
                  required 
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent outline-none transition-all text-sm"
                />
              </div>
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">Message</label>
                <textarea 
                  id="message" 
                  name="message" 
                  rows={6} 
                  required 
                  maxLength={500}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent outline-none transition-all resize-none text-sm"
                ></textarea>
                <p className="text-xs text-gray-500 mt-2">Maximum 500 characters</p>
              </div>
              <button 
                type="submit" 
                className="bg-gray-900 text-white px-8 py-3 rounded-lg font-medium hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
              >
                Send Message
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1A3A2E] py-16">
        <div className="max-w-6xl mx-auto px-8">
          <div className="grid grid-cols-4 gap-12 mb-16">
            {/* Company Info */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <img src="https://public.readdy.ai/ai/img_res/56d1bb7a-176a-4552-b93a-e2669969f21d.png" alt="PROTIM D.O.O." className="h-8 w-8 object-contain" />
                <span className="text-lg font-bold text-white">PROTIM D.O.O.</span>
              </div>
              <p className="text-sm text-gray-400 leading-relaxed">
                Expert team developing modern iOS applications for enhanced security and performance
              </p>
            </div>

            {/* Products */}
            <div>
              <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">Products</h4>
              <ul className="space-y-3">
                <li><span className="text-sm text-white cursor-pointer hover:text-gray-300 transition-colors">VPN</span></li>
                <li><span className="text-sm text-white cursor-pointer hover:text-gray-300 transition-colors">Cleaner</span></li>
                <li><span className="text-sm text-white cursor-pointer hover:text-gray-300 transition-colors">Coming Soon</span></li>
              </ul>
            </div>

            {/* Company */}
            <div>
              <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">Company</h4>
              <ul className="space-y-3">
                <li><button onClick={() => scrollToSection('about')} className="text-sm text-white cursor-pointer hover:text-gray-300 transition-colors">About</button></li>
                <li><span className="text-sm text-white cursor-pointer hover:text-gray-300 transition-colors">Team</span></li>
                <li><span className="text-sm text-white cursor-pointer hover:text-gray-300 transition-colors">Careers</span></li>
              </ul>
            </div>

            {/* Connect */}
            <div>
              <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">Connect</h4>
              <ul className="space-y-3">
                <li><span className="text-sm text-white cursor-pointer hover:text-gray-300 transition-colors">Twitter</span></li>
                <li><span className="text-sm text-white cursor-pointer hover:text-gray-300 transition-colors">LinkedIn</span></li>
                <li><span className="text-sm text-white cursor-pointer hover:text-gray-300 transition-colors">GitHub</span></li>
              </ul>
            </div>
          </div>

          {/* Large Typography */}
          <div className="border-t border-gray-700 pt-12">
            <div className="flex items-center justify-between">
              <div className="text-8xl font-bold text-[#E8DCC4] tracking-tight" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 900 }}>
                PROTIM
              </div>
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-sm text-gray-400 hover:text-gray-300 transition-colors cursor-pointer">
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
